import isEqual from 'lodash/isEqual';

import type { DynamicRule, PageInstanceMapping } from 'clients/BrowserExtensionClient/BrowserExtension.types';

import {
  getDynamicRulesConfig,
  interceptRules,
  pageInstanceMapping,
} from 'clients/BrowserExtensionClient/browserExtension.config';
import { browserExtensionClient } from 'clients/BrowserExtensionClient/BrowserExtensionClient';
import { getExtensionStatus } from 'hooks/shared/useBrowserExtensionStatusQuery';

const SESSION_RULES_MIN_EXTENSION_VERSION = '1.7.17';

export const updateExtensionSettings = async () => {
  const [currentMapping, currentDynamicRules, extensionStatus] = await Promise.all([
    browserExtensionClient.sendMessage<{ data: PageInstanceMapping | null }>('getPageInstanceMapping'),
    browserExtensionClient.sendMessage<{ data: DynamicRule[] | null }>('getDynamicRules'),
    getExtensionStatus(),
  ]);

  await browserExtensionClient.sendMessage('setInterceptRules', {
    interceptRules,
  });

  if (!isEqual(currentMapping.data, pageInstanceMapping)) {
    await browserExtensionClient.sendMessage('setPageInstanceMapping', pageInstanceMapping);
  }

  const userPlatform = getUserPlatform();
  const userBrands = getUserBrands();
  const dynamicRulesConfig = getDynamicRulesConfig(userPlatform, userBrands, {
    useSessionRules: isVersionGreaterThanOrEqualTo(extensionStatus.version, SESSION_RULES_MIN_EXTENSION_VERSION),
  });

  if (!isEqual(currentDynamicRules.data, dynamicRulesConfig)) {
    await browserExtensionClient.sendMessage('setDynamicRules', dynamicRulesConfig);
  }
};

const getUserPlatform = () => {
  if (!navigator.userAgentData) {
    return '';
  }
  return navigator.userAgentData.platform || '';
};

const getUserBrands = () => {
  if (!navigator.userAgentData) {
    return '';
  }
  // Prevent duplicate "Not A(Brand"
  const userBrandsSet = new Set(
    navigator.userAgentData?.brands.map((brand) => `"${brand.brand}";v="${brand.version}"`),
  );
  userBrandsSet.add(`"Not A(Brand";v="8"`);

  return Array.from(userBrandsSet).join(', ');
};

const isVersionGreaterThanOrEqualTo = (version = '', minVersion: string) => {
  const versionParts = version.split('.').map(Number);
  const minVersionParts = minVersion.split('.').map(Number);

  for (let i = 0; i < minVersionParts.length; i++) {
    const versionPart = versionParts[i] || 0;
    const minVersionPart = minVersionParts[i] || 0;

    if (versionPart > minVersionPart) return true;
    if (versionPart < minVersionPart) return false;
  }

  return true;
};
